import apisauce, { ApiResponse } from 'apisauce'
import { AxiosProgressEvent } from 'axios'
import cookie from 'cookie'
import type {
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser'
import { API_URL } from '../constants'
import { Tag } from '../components/SearchMenu/SearchMenu'
import AlbumModel from '../models/album'
import { setupAuthInterceptors } from './authInterceptor'
import {
  IPopulatedUserPhoto,
  IUserPhoto,
  LoginResult,
  LoggedIn,
  OtpResponse,
  IUser,
} from '../models/user_model'
import {
  PasskeyCredentialSummary,
  PasskeyLoginOptions,
  PasskeyApiErrorBody,
  PublicKeyCredentialCreationOptionsJSON,
} from '../models/passkey'

// Re-export ApiResponse for external use
export type { ApiResponse } from 'apisauce'

interface ErrorResponse {
  msg?: string
}

interface SendPhotoConfig {
  photo: File
  albumId?: string
  isPublic?: boolean
  workspaceId: string
}

export interface User {
  id: string
  email: string
  avatar?: string
  firstName?: string
  lastName?: string
  username: string
  isAdmin: boolean
  themeColor?: string
  age?: number
  photoLimit: number | string
  accountConfirmed: boolean
  tagsCount: number
  albumsCount: number
  photosCount: Number
}

type GetCurrentUserCall = () => Promise<ApiResponse<IUser>>
type RegisterCall = (config: {
  username: string
  password: string
  email: string
  firstName?: string
}) => Promise<ApiResponse<any>>
type FetchPhotosCall = (options: {
  page: number
  perPage: number
  workspaceId: string
  albumId?: string
  query?: string
  publicPhotos?: boolean
}) => Promise<ApiResponse<Array<IUserPhoto>>>

// ============================================
// Resource Types (v3 API)
// ============================================

export type ResourceType = 'photo' | 'image' | 'video' | 'unsupported'
// `date_liked` needs is_favorite and `date_added_to_album` needs album_id, otherwise the
// server silently falls back to date_taken.
export type ResourcesOrdering =
  | 'date_taken'
  | 'date_uploaded'
  | 'date_liked'
  | 'date_added_to_album'
  | 'random'
export type OrderType = 'asc' | 'desc'

export interface RemoteResource {
  id: string
  type: ResourceType
  name: string
  width: number
  height: number
  blurhash: string | null
  owner_id: string
  workspace_id: string
  is_favorite: boolean
  duration: number | null // in milliseconds
  /** timeline group date (date_taken ?? created_at); null on legacy rows */
  date: string | null
  url: string
}

/**
 * Build a sized resource URL by appending /small, /large, or /original
 * to the base resource URL returned by the API.
 */
export const getResourceUrl = (
  resource: RemoteResource,
  size: 'small' | 'large' | 'original'
): string => `${resource.url}/${size}`

// Video Streams
export type VideoStreamType = 'hls' | 'dash' | 'unsupported'

export interface VideoStream {
  id: string
  type: VideoStreamType
  size: number
  resource_id: string
  url: string
  signature: string
}

export interface PaginatedData<T> {
  elements: Array<T>
  count: number
  from: number
  to: number
  /** false when the request failed — lets callers tell an error apart from a genuinely empty page */
  ok: boolean
}

// ============================================
// Timeline Types
// ============================================

export enum TimelinePermissions {
  Read = 0,
  Self = 1,
  Write = 2,
  Delete = 3,
  Maintainer = 4,
  Owner = 10,
}

export interface RemoteResourceDefinition {
  id: string
  urls: {
    base: string
    small: string
    large: string
    original: string
  }
}

export interface Timeline {
  id: string
  name: string
  description?: string
  color: string
  workspace_id: string
  cover_photo_id?: string
  cover_photo?: RemoteResourceDefinition
  posts_count: number
  editors_count: number
  created_at: string
  updated_at: string
  permissions: TimelinePermissions
}

// ============================================
// Emoji Types
// ============================================

export interface EmojiOwner {
  id: string
  first_name: string
  last_name: string
}

export interface Emoji {
  id: string
  name: string
  owner: EmojiOwner
  image_url: string
  workspace_id: string
  created_at: string
}

// ============================================
// Post Types
// ============================================

export type RecordingType = 'voice' | 'music' | 'post' | 'other'
export type RecordingFormat = 'mp3' | 'wav' | 'aac' | 'ogg' | 'webm' | 'flac'

export interface Recording {
  id: string
  workspace_id: string
  owner_id: string
  filename: string
  duration: number
  size: number
  url: string
  signature: string
  type: RecordingType
  format: RecordingFormat
  title?: string | null
}

export interface PostAuthor {
  id: string
  first_name?: string
  last_name?: string
  color: string
  avatar?: string
  avatar_id?: string | null
  last_action_at?: string
  is_men?: boolean
  role?: number
  can_set_app_cover?: boolean
}

export interface PostReaction {
  reaction: string
  users: Array<string>
}

export interface PostComment {
  id: string
  post_id: string
  author_id: string
  content: string
  created_at: string
  updated_at: string
  author: PostAuthor
}

export interface Post {
  id: string
  title: string
  content?: string
  location?: string
  lat?: number
  lng?: number
  alt?: number
  color: string
  author_id: string
  author: PostAuthor
  recording?: Recording | null
  resources: Array<RemoteResource>
  reactions: Array<PostReaction>
  comments?: Array<PostComment>
  date: string
  created_at: string
  updated_at: string
}

export interface FetchResourcesOptions {
  workspaceId: string
  from: number
  count: number
  orderBy?: ResourcesOrdering
  order?: OrderType
  public?: boolean
  tags?: Array<string> // tag IDs
  personIds?: Array<string>
  albumId?: string
  deviceId?: string
  location?: { lat: number; lng: number; radius: number }
  placeId?: string
  ownerIds?: Array<string>
  type?: ResourceType
  dateFrom?: Date
  dateTo?: Date
  isFavorite?: boolean
  query?: string
  embeddingId?: string
  /** timeline bucket key ("YYYY" | "YYYY-MM" | "YYYY-MM-DD") — returns only that bucket's slice */
  bucket?: string
  /** required by the server when bucket is set */
  timezone?: string
}

// Timeline endpoint types — bucket counts are newest-first and match the
// /resources list order 1:1, so flat offsets are computable from prefix sums.
export interface TimelineDayBucket {
  key: string // "YYYY-MM-DD" in the requested timezone
  count: number
}

export interface TimelineMonthBucket {
  key: string // "YYYY-MM"
  count: number
  days: Array<TimelineDayBucket>
}

export interface TimelineYearBucket {
  key: string // "YYYY"
  count: number
  months: Array<TimelineMonthBucket>
}

export interface ResourceTimeline {
  timezone: string
  order_by: TimelineOrderBy
  total: number
  years: Array<TimelineYearBucket>
}

export type TimelineOrderBy = 'date_taken' | 'date_uploaded'

export type FetchResourceTimelineOptions = Omit<
  FetchResourcesOptions,
  'from' | 'count' | 'query' | 'embeddingId' | 'orderBy' | 'order'
> & {
  timezone: string
  orderBy?: TimelineOrderBy
}

export interface IDevice {
  description?: string
  id: string
  make: string
  model: string
  name?: string
  type: 'camera' | 'smartphone'
  photos_count?: number
}

export interface IPerson {
  id: string
  name: string
  sex?: string | null
  cover_url?: string | null
  workspace_id: string
  user_id?: string | null
  birthdate?: string | null
}

export interface IWorkspace {
  id: string
  name?: string
  owner_id: string
  color: string
  slug: string
  permissions: number
}

export interface IWorkspaceMember {
  id: string
  first_name: string
  last_name: string
  color: string
  avatar?: string | null
  permissions?: number
  photos_count?: number
}

export interface IWorkspaceDetails extends IWorkspace {
  users: Array<IWorkspaceMember>
}

// Re-export IUser from models
export type { IUser, LoginResult, OtpResponse } from '../models/user_model'

export interface ApiError {
  message: string
  details: any
}

const create = (baseURL = API_URL) => {
  // Access token lives in memory as the source of truth.
  // A cookie is used only as a cache so the token survives page reloads
  // without requiring a refresh call every time.
  // On startup, the interceptor will still call /auth/refresh to verify
  // the token is valid.
  let accessToken: string | undefined = cookie.parse(document.cookie).auth || undefined

  const getToken = () => accessToken

  const api = apisauce.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    withCredentials: true,
    // Arrays as repeated keys (person_id=a&person_id=b) — the API's Express 5 "simple"
    // query parser doesn't understand axios' default bracket form (person_id[]=a).
    paramsSerializer: { indexes: null },
  })

  // Setup auth interceptors
  const { tokenManager } = setupAuthInterceptors(api.axiosInstance, {
    getToken,
    setToken: (token) => {
      accessToken = token
      document.cookie = `auth=${token}; ;path=/`
    },
    deleteToken: () => {
      accessToken = undefined
      deleteCookie('auth')
    },
  })

  api.addResponseTransform((response) => {
    if (!response.headers) {
      response.data = {}
      if (response.problem) {
        response.data.error = response.problem
      } else {
        response.data.error = 'Server is down'
      }
      return
    }
    const { authorization: authToken } = response.headers as any
    if (!authToken) return

    // Store the new access token in memory and cache in cookie
    accessToken = authToken
    document.cookie = `auth=${authToken}; ;path=/`

    // Cache the new token's expiration
    tokenManager.updateFromToken(authToken)

    setHeaders({
      Authorization: authToken,
    })
  })

  const setHeaders = (headersObject) => {
    api.setHeaders(headersObject)
  }

  // Auth endpoints
  const login = (username: string, password: string): Promise<ApiResponse<LoginResult, ApiError>> =>
    api.post('/auth/login', { login: username, password })

  const authWithOtp = (
    sessionId: string,
    otp: string
  ): Promise<ApiResponse<OtpResponse, ApiError>> =>
    api.post('/auth/otp', { session_id: sessionId, otp })

  const unblockUser = (email: string, code: string): Promise<ApiResponse<void, ApiError>> =>
    api.post('/auth/unblock', { email, code })

  // Public (no login): the API always answers 202 so it never reveals whether the account exists.
  const requestAccountDeletion = (email: string): Promise<ApiResponse<void, ApiError>> =>
    api.delete('/config/user/delete-account', {}, { data: { email } })

  const confirmAccountDeletion = (token: string): Promise<ApiResponse<void, ApiError>> =>
    api.post('/config/user/delete-account/confirm', { token })

  // Logout - refresh token is sent automatically via HTTP-only cookie
  const logout = (): Promise<ApiResponse<void, ApiError>> =>
    api.delete('/auth/logout', undefined, {
      withCredentials: true,
      headers: {
        // Add it explicitly to make sure it's included
        Authorization: getToken(),
      },
    })

  const refreshToken = (): Promise<ApiResponse<{ access_token: string }, ApiError>> =>
    api.post('/auth/refresh', undefined, {
      withCredentials: true,
    })

  // Passkeys: registration ceremony (requires an active session)
  const getPasskeyRegistrationOptions = (): Promise<
    ApiResponse<PublicKeyCredentialCreationOptionsJSON, PasskeyApiErrorBody>
  > => api.post('/auth/passkeys/register/options', {})

  const verifyPasskeyRegistration = (
    response: RegistrationResponseJSON,
    name?: string
  ): Promise<ApiResponse<PasskeyCredentialSummary, PasskeyApiErrorBody>> =>
    api.post('/auth/passkeys/register/verify', name ? { name, response } : { response })

  // Passkeys: login ceremony (public, usernameless)
  const getPasskeyLoginOptions = (): Promise<
    ApiResponse<PasskeyLoginOptions, PasskeyApiErrorBody>
  > =>
    // Body must be exactly {} — the endpoint schema forbids extra properties
    api.post('/auth/passkeys/login/options', {})

  const verifyPasskeyLogin = (
    sessionId: string,
    response: AuthenticationResponseJSON
  ): Promise<ApiResponse<LoggedIn, PasskeyApiErrorBody>> =>
    api.post('/auth/passkeys/login/verify', { session_id: sessionId, response })

  // Passkeys: management
  const listPasskeys = (): Promise<
    ApiResponse<{ credentials: Array<PasskeyCredentialSummary> }, PasskeyApiErrorBody>
  > => api.get('/auth/passkeys')

  const renamePasskey = (
    id: string,
    name: string
  ): Promise<ApiResponse<PasskeyCredentialSummary, PasskeyApiErrorBody>> =>
    api.patch(`/auth/passkeys/${id}`, { name })

  const deletePasskey = (id: string): Promise<ApiResponse<void, PasskeyApiErrorBody>> =>
    api.delete(`/auth/passkeys/${id}`)

  // TODO:
  const register: RegisterCall = ({ username, password, email, firstName }) =>
    api.post('/user/register', { email, password, username, firstName })

  const getCurrentUser: GetCurrentUserCall = () =>
    api.get('/users/current')
  const uploadPhoto = (
    { photo, albumId, isPublic, workspaceId }: SendPhotoConfig,
    onUploadProgress?: (event: AxiosProgressEvent) => void
  ) => {
    const form = new FormData()
    form.append('photo', photo)
    if (isPublic !== undefined) form.append('is_public', String(isPublic))
    if (albumId !== undefined) form.append('album_id', String(albumId))
    const headers = {
      'Content-Type': 'multipart/form-data',
    }
    return api.post(`/${workspaceId}/photos`, form, { headers, onUploadProgress })
  }

  const fetchPhotos: FetchPhotosCall = ({
    workspaceId,
    page,
    perPage,
    albumId,
    publicPhotos,
    query,
  }) =>
    api.get(`/${workspaceId}/photos`, {
      from: page * perPage,
      count: perPage,
      album_id: albumId,
      query,
      public: publicPhotos,
    })

  // Filters shared by the /resources list and /resources/timeline endpoints
  const buildResourceFilterParams = (
    options: Omit<FetchResourceTimelineOptions, 'timezone' | 'orderBy' | 'workspaceId'>
  ): Record<string, string | Array<string>> => {
    const params: Record<string, string | Array<string>> = {}
    if (options.public !== undefined) params.public = options.public.toString()
    if (options.tags?.length) params.tag = options.tags
    if (options.personIds?.length) params.person_id = options.personIds
    if (options.albumId) params.album_id = options.albumId
    if (options.deviceId) params.device_id = options.deviceId
    if (options.location) {
      params.lat = options.location.lat.toString()
      params.lng = options.location.lng.toString()
      params.radius = options.location.radius.toString()
    }
    if (options.placeId) params.place_id = options.placeId
    if (options.ownerIds?.length) params.user_id = options.ownerIds
    if (options.type && options.type !== 'unsupported') params.type = options.type
    if (options.dateFrom) params.date_from = options.dateFrom.toISOString()
    if (options.dateTo) params.date_to = options.dateTo.toISOString()
    if (options.isFavorite !== undefined) params.is_favorite = options.isFavorite.toString()
    return params
  }

  const fetchResources = async (
    options: FetchResourcesOptions
  ): Promise<PaginatedData<RemoteResource>> => {
    const queryParams: Record<string, string | Array<string>> = {
      ...buildResourceFilterParams(options),
      from: options.from.toString(),
      count: options.count.toString(),
      order_by: options.orderBy || 'date_taken',
    }

    if (options.order) queryParams.order = options.order
    if (options.query && !options.embeddingId) queryParams.query = options.query
    if (options.embeddingId) queryParams.embedding_id = options.embeddingId
    if (options.bucket) {
      queryParams.bucket = options.bucket
      if (options.timezone) queryParams.timezone = options.timezone
    }

    // queryParams.signed = 'true'

    const response = await api.get<Array<RemoteResource>>(
      `/v3/${options.workspaceId}/resources`,
      queryParams
    )

    if (!response.ok || !response.data) {
      return { elements: [], count: 0, from: 0, to: 0, ok: false }
    }

    const headers = response.headers || {}
    return {
      elements: response.data,
      count: parseInt(headers['x-pagination-count'] || '0', 10),
      from: parseInt(headers['x-pagination-from'] || '0', 10),
      to: parseInt(headers['x-pagination-to'] || '0', 10),
      ok: true,
    }
  }

  // Returns null on failure so callers can fall back to plain sequential paging.
  const fetchResourceTimeline = async (
    options: FetchResourceTimelineOptions
  ): Promise<ResourceTimeline | null> => {
    const { workspaceId, timezone, orderBy, ...filters } = options
    const response = await api.get<ResourceTimeline>(`/v3/${workspaceId}/resources/timeline`, {
      ...buildResourceFilterParams(filters),
      timezone,
      order_by: orderBy || 'date_taken',
    })

    if (!response.ok || !response.data) return null
    return response.data
  }

  const getWorkspaces = (): Promise<ApiResponse<Array<IWorkspace>, ApiError>> =>
    api.get('/workspaces')

  const getWorkspace = (workspaceId: string): Promise<ApiResponse<IWorkspaceDetails, ApiError>> =>
    api.get(`/workspaces/${workspaceId}`)

  const getPersons = ({
    workspaceId,
    phrase,
    from,
    count,
  }: {
    workspaceId: string
    phrase?: string
    from?: number
    count?: number
  }): Promise<ApiResponse<Array<IPerson>, ApiError>> =>
    api.get(`/${workspaceId}/persons`, {
      ...(phrase ? { phrase } : {}),
      ...(from !== undefined ? { from: from.toString() } : {}),
      ...(count !== undefined ? { count: count.toString() } : {}),
    })

  const getPerson = ({
    workspaceId,
    id,
  }: {
    workspaceId: string
    id: string
  }): Promise<ApiResponse<IPerson, ApiError>> => api.get(`/${workspaceId}/persons/${id}`)

  const getDevices = ({
    workspaceId,
    phrase,
  }: {
    workspaceId: string
    phrase?: string
  }): Promise<ApiResponse<Array<IDevice>, ApiError>> =>
    api.get(`/${workspaceId}/devices`, phrase ? { phrase } : {})

  const getPhoto = (
    workspaceId: string,
    photoId: string
  ): Promise<ApiResponse<IPopulatedUserPhoto, ApiError>> =>
    api.get(`/${workspaceId}/photos/${photoId}`)

  const updatePhoto = (
    photoId: string,
    {
      isPublic,
      description,
      tags,
      albumId,
      workspaceId,
    }: {
      workspaceId: string
      isPublic?: boolean
      description?: string
      tags: Array<String>
      albumId?: string
    }
  ): Promise<ApiResponse<IPopulatedUserPhoto, ApiError>> =>
    api.put(`/${workspaceId}/photos/${photoId}`, {
      public: isPublic,
      description,
      tags,
      album_id: albumId,
    })

  const updateAlbum = (
    albumId: string | number,
    {
      cover,
      name,
      description,
      isPublic,
      workspaceId,
    }: {
      cover?: File
      name?: string
      description?: string
      isPublic?: boolean
      workspaceId: string
    },
    onUploadProgress?: (event: AxiosProgressEvent) => void
  ): Promise<ApiResponse<AlbumModel, ApiError>> => {
    const form = new FormData()

    if (cover) form.append('cover', cover)
    if (typeof name === 'string') form.append('name', name)
    if (typeof description === 'string') form.append('description', description)
    if (typeof isPublic === 'boolean') form.append('is_public', `${isPublic}`)

    const headers = {
      'Content-Type': 'multipart/form-data',
    }

    return api.put(`/${workspaceId}/albums/${albumId}`, form, { headers, onUploadProgress })
  }

  const createAlbum = ({
    name,
    description,
    isPublic,
    workspaceId,
  }: {
    isPublic: boolean
    name: string
    description: string
    workspaceId: string
  }): Promise<ApiResponse<AlbumModel, ApiError>> =>
    api.post(`/${workspaceId}/albums`, { name, description, is_public: isPublic })

  const getTags = (
    workspaceId: string,
    phrase: string,
    limit?: number
  ): Promise<ApiResponse<Array<Tag>, ApiError>> =>
    api.get(`/${workspaceId}/tags`, { phrase, limit })

  const getAlbums = ({
    phrase,
    count,
    from = 0,
    workspaceId,
    publicAlbums = false,
  }: {
    workspaceId: string
    phrase?: string
    from?: number
    count: number
    publicAlbums?: boolean
  }): Promise<ApiResponse<Array<AlbumModel>, ApiError>> =>
    api.get(`/${workspaceId}/albums`, { phrase, count, from, public: publicAlbums })

  const getAlbum = ({
    id,
    workspaceId,
  }: {
    workspaceId: string
    id: string
  }): Promise<ApiResponse<AlbumModel, ApiError>> => api.get(`/${workspaceId}/albums/${id}`)

  const getAlbumDownloadUrl = ({
    id,
    workspaceId,
  }: {
    workspaceId: string
    id: string
  }): Promise<ApiResponse<{ url: string; filename: string }, ApiError>> =>
    api.get(`/${workspaceId}/albums/${id}/photos/download-url`)

  const deleteAlbum = ({
    id,
    workspaceId,
  }: {
    id: string
    workspaceId: string
  }): Promise<ApiResponse<AlbumModel, ApiError>> => api.delete(`/${workspaceId}/albums/${id}`)

  const deleteUserPhoto = ({
    id,
    workspaceId,
  }: {
    workspaceId: string
    id: string
  }): Promise<ApiResponse<null, ApiError>> =>
    api.delete(
      `/${workspaceId}/photos`,
      {},
      {
        data: {
          photos: id,
        },
      }
    )

  const getVideoStreams = ({
    workspaceId,
    resourceId,
  }: {
    workspaceId: string
    resourceId: string
  }): Promise<ApiResponse<Array<VideoStream>, ApiError>> =>
    api.get(`/${workspaceId}/resources/${resourceId}/videos`)

  const getTimelines = ({
    workspaceId,
    from,
    count,
    phrase,
  }: {
    workspaceId: string
    from?: number
    count?: number
    phrase?: string
  }): Promise<ApiResponse<Array<Timeline>, ApiError>> =>
    api.get(`/v2/${workspaceId}/timelines`, {
      ...(from !== undefined && { from }),
      ...(count !== undefined && { count }),
      ...(phrase !== undefined && { phrase }),
    })

  const getTimelinePosts = ({
    workspaceId,
    timelineId,
    from,
    count,
    order = 'desc',
    phrase,
  }: {
    workspaceId: string
    timelineId: string
    from: number
    count: number
    order?: OrderType
    phrase?: string
  }): Promise<ApiResponse<Array<Post>, ApiError>> =>
    api.get(`/${workspaceId}/timelines/${timelineId}/posts`, {
      from,
      count,
      order,
      ...(phrase !== undefined && { phrase }),
    })

  /**
   * Get URL for resized photo download
   * Uses the resized endpoint which returns optimized images
   */
  const getResizedPhotoUrl = ({
    workspaceId,
    resourceId,
    width,
    height,
    format,
    quality,
  }: {
    workspaceId: string
    resourceId: string
    width?: number
    height?: number
    format?: 'webp' | 'jpeg' | 'png' | 'avif'
    quality?: number
  }): string => {
    const params = new URLSearchParams()
    if (width) params.set('width', width.toString())
    if (height) params.set('height', height.toString())
    if (format) params.set('format', format)
    if (quality) params.set('quality', quality.toString())
    return `${baseURL}/${workspaceId}/resources/${resourceId}/resized?${params.toString()}`
  }

  const getEmojis = ({
    workspaceId,
  }: {
    workspaceId: string
  }): Promise<ApiResponse<Array<Emoji>, ApiError>> =>
    api.get(`/${workspaceId}/emojis`)

  return {
    createAlbum,
    updateAlbum,
    deleteUserPhoto,
    getVideoStreams,
    getEmojis,
    getTimelines,
    getTimelinePosts,
    getResizedPhotoUrl,
    login,
    authWithOtp,
    unblockUser,
    requestAccountDeletion,
    confirmAccountDeletion,
    logout,
    refreshToken,
    getPasskeyRegistrationOptions,
    verifyPasskeyRegistration,
    getPasskeyLoginOptions,
    verifyPasskeyLogin,
    listPasskeys,
    renamePasskey,
    deletePasskey,
    getCurrentUser,
    uploadPhoto,
    fetchPhotos,
    fetchResources,
    fetchResourceTimeline,
    getAlbums,
    getAlbum,
    deleteAlbum,
    updatePhoto,
    register,
    getPhoto,
    getTags,
    getToken,
    getWorkspaces,
    getWorkspace,
    getPersons,
    getPerson,
    getDevices,
    getAlbumDownloadUrl,
  }
}

export class API {
  public static getInstance() {
    if (this._instance === null) this._instance = create()
    return this._instance
  }
  private static _instance = create()
}

export const deleteCookie = (cookieName: string) => {
  const date = new Date()
  date.setTime(date.getTime() - 1000 * 60 * 60 * 24)
  const expires = 'expires=' + date.toUTCString()
  document.cookie = `${cookieName}=null; ${expires}; path=/`
}
